import { cva } from 'class-variance-authority';
import type { VariantProps } from 'class-variance-authority';
import { CheckCircle2, Circle, OctagonX, TriangleAlert } from 'lucide-react';
import type { HTMLAttributes } from 'react';

const badgeVariants = cva('inline-flex items-center gap-1 rounded border tabular-nums', {
  variants: {
    tone: {
      success: 'border-status-success-border bg-status-success-surface text-status-success',
      warning: 'border-status-warning-border bg-status-warning-surface text-status-warning',
      danger: 'border-status-danger-border bg-status-danger-surface text-status-danger',
      neutral: 'border-status-neutral-border bg-status-neutral-surface text-status-neutral',
    },
    size: {
      sm: 'px-2 py-1 text-type-card-label [&>svg]:h-3 [&>svg]:w-3',
      md: 'px-3 py-2 text-type-secondary [&>svg]:h-4 [&>svg]:w-4',
      lg: 'px-3 py-2 text-type-metric-value [&>svg]:h-5 [&>svg]:w-5',
    },
  },
  defaultVariants: {
    size: 'sm',
    tone: 'neutral',
  },
});

interface BadgeProps extends HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeVariants> {}

const toneIcons = {
  success: CheckCircle2,
  warning: TriangleAlert,
  danger: OctagonX,
  neutral: Circle,
};

export function Badge({
  children,
  className,
  size = 'sm',
  tone = 'neutral',
  ...props
}: BadgeProps) {
  const Icon = toneIcons[tone ?? 'neutral'];

  return (
    <span className={`${badgeVariants({ size, tone })} ${className ?? ''}`.trim()} {...props}>
      <Icon className="shrink-0" aria-hidden="true" />
      {children}
    </span>
  );
}
